/**
 * SB6-II-01 — Add Inspection Instruction modal.
 *
 * Role-gated (TA / PM / platform_admin — see `InspectionInstructionsPage`
 * for the matrix-key rationale). Layout mirrors the Edit modal. On open
 * only Project is enabled; the Project → Contract → Contract Position
 * cascade enables each step (changing Project clears downstream — no
 * lock-in, AC17). Picking a Position enables ai flags + notes (live
 * "{n} of {max}" counter) + external ids. Buttons: Save, Cancel.
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useToast } from "../account/ToastContext.js";
import { ExternalIdsField } from "../sites/shared/ExternalIdsField.js";
import {
  ProjectContractPositionCascade,
  type CascadeValue,
} from "./ProjectContractPositionCascade.js";
import { useCreateInspectionInstructionMutation } from "./mutations.js";
import { II_NOTES_MAX, type IiExternalId } from "./types.js";

interface AddFormValues {
  aiFindingRequired: boolean;
  aiComponentSegmentationRequired: boolean;
  notes: string;
  externalIds: IiExternalId[];
}

function dropEmptyExternalIds(arr: IiExternalId[]): IiExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

export interface AddInspectionInstructionDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddInspectionInstructionDialog({
  open,
  onClose,
}: AddInspectionInstructionDialogProps): ReactElement {
  const { toast } = useToast();
  const create = useCreateInspectionInstructionMutation();
  const [cascade, setCascade] = useState<CascadeValue>({
    projectId: null,
    contractId: null,
    contractPositionId: null,
  });

  const { register, handleSubmit, reset, watch, control } =
    useForm<AddFormValues>({
      defaultValues: {
        aiFindingRequired: false,
        aiComponentSegmentationRequired: false,
        notes: "",
        externalIds: [],
      },
    });

  const notes = watch("notes");
  const aiFindingRequired = watch("aiFindingRequired");
  const aiComponentSegmentationRequired = watch(
    "aiComponentSegmentationRequired",
  );

  const positionPicked = cascade.contractPositionId !== null;

  function closeAndReset(): void {
    reset();
    setCascade({
      projectId: null,
      contractId: null,
      contractPositionId: null,
    });
    onClose();
  }

  function onSubmit(v: AddFormValues): void {
    if (cascade.contractPositionId === null) return;
    create.mutate(
      {
        contractPositionId: cascade.contractPositionId,
        aiFindingRequired: v.aiFindingRequired,
        aiComponentSegmentationRequired: v.aiComponentSegmentationRequired,
        ...(v.notes.trim().length > 0 ? { notes: v.notes } : {}),
        externalIds: dropEmptyExternalIds(v.externalIds),
      },
      {
        onSuccess: () => {
          toast("Inspection instruction created.");
          closeAndReset();
        },
        onError: () => {
          toast("Couldn't create inspection instruction. Try again.", "error");
        },
      },
    );
  }

  const counter = useMemo(
    () => `${String(notes.length)} of ${String(II_NOTES_MAX)}`,
    [notes],
  );

  return (
    <Dialog
      open={open}
      onClose={closeAndReset}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-ii-heading"
    >
      <DialogTitle id="add-ii-heading">Add Inspection Instruction</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Link
          </Typography>
          <ProjectContractPositionCascade
            value={cascade}
            onChange={setCascade}
            enabled={open}
          />

          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" color="text.secondary">
            Details
          </Typography>
          {/* Cascade gating: the rest enables only after a Position is
              picked (AC17). */}
          <Box sx={{ opacity: positionPicked ? 1 : 0.5 }}>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    {...register("aiFindingRequired")}
                    checked={aiFindingRequired}
                    disabled={!positionPicked}
                  />
                }
                label="AI finding required"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    {...register("aiComponentSegmentationRequired")}
                    checked={aiComponentSegmentationRequired}
                    disabled={!positionPicked}
                  />
                }
                label="AI component segmentation required"
              />
              <TextField
                {...register("notes", { maxLength: II_NOTES_MAX })}
                label="Notes"
                multiline
                rows={3}
                disabled={!positionPicked}
                helperText={counter}
                slotProps={{
                  htmlInput: {
                    "aria-label": "Notes",
                    maxLength: II_NOTES_MAX,
                  },
                }}
                fullWidth
              />
              <ExternalIdsField
                control={control}
                register={register}
                readOnly={!positionPicked}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeAndReset} disabled={create.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit(onSubmit)();
          }}
          disabled={!positionPicked || create.isPending}
          startIcon={
            create.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
